/**
 * `WsEvent` discriminated union — every variant the broadcast lane
 * can surface, plus the dispatcher helpers (`WsEventKind`,
 * `ALL_WS_EVENT_KINDS`, `KNOWN_WS_EVENT_KINDS`) derived from it.
 *
 * Forward-compat is preserved by the parser's graceful-skip gate
 * (`ws/parse.ts::parseWsFrame` consults `KNOWN_WS_EVENT_KINDS`):
 * an unknown `kind` on a higher-minor server lands as `null` so
 * the reducer never sees a half-narrowed payload. Adding a new
 * variant requires (1) extending the union below, (2) updating
 * `ALL_WS_EVENT_KINDS`, and (3) mapping a reducer in
 * `store/game/store.ts::WS_EVENT_REDUCERS` — both (2) and (3) are
 * exhaustiveness-checked at TS compile time, so a forgotten
 * step lands as a TS error.
 */

import type { WsLane, PlayerIdHex } from "./protocol";
import type {
  ActionView,
  AwardView,
  BlindsView,
  CardView,
  HandShowdownSeatView,
  LegalActionSetView,
  PlayerLeftReason,
  PotTier,
  RoundCloseReasonView,
  SeatOwnerSlot,
  SeatStackView,
  SeatStartedView,
  SettledInvestedView,
  StageView,
} from "./views";
import type { SeedAbortReason, SeedRoundView } from "./seed";
import type { HandSnapshot, SpectatorHandSnapshot } from "./snapshots";

/**
 * Discriminated union of all wire variants. The 9 alternatives match
 * ADR-007 v0.4 §Variants 1:1; any extra branch on the server side
 * surfaces as an `Unknown` runtime case via `parseWsEvent` so a
 * higher-minor server can ship forward-compatible additions without
 * tripping the client (ADR-007 §Implementation guidance unknown
 * variant graceful skip).
 */
export type WsEvent =
  // framing
  | {
      readonly kind: "protocol_version";
      readonly major: number;
      readonly minor: number;
    }
  | { readonly kind: "heartbeat"; readonly server_unix_ms: number }
  | { readonly kind: "lagged"; readonly dropped: number }
  | { readonly kind: "shutdown"; readonly reason: string }
  // game
  | {
      readonly kind: "hand_started";
      readonly hand_id: number;
      readonly hand_seq: number;
      readonly button: number;
      readonly blinds: BlindsView;
      readonly seats: ReadonlyArray<SeatStartedView>;
      readonly initial_to_act: number;
      readonly round_bet: number;
      readonly actions_taken: number;
      /** Wire minor 34 — sorted-ascending seat indices the engine
       *  DEALT IN for this hand (the frozen `deal_eligible` roster
       *  captured server-side at `finalize_seed_and_start_hand`,
       *  sourced from the SAME phase-frozen `InHand.dealt_seats` value
       *  `HandSnapshot.dealt_seats` already carries — the two cannot
       *  diverge). This is the SOLE deal-cursor authority the
       *  hole-card-index derive walks, on the LIVE path as well as the
       *  reconnect Snapshot. It is NARROWER than `seats` (the occupied
       *  roster) whenever an occupied seat is SittingOut / zero-stack at
       *  hand start: that seat is occupied but NOT dealt, so deriving
       *  the cursor from the occupied set would shift every hero's
       *  hole-card index. Optional for forward-compat: a pre-bump server
       *  omits the field and the reducer falls back to the occupied-set
       *  behavior. */
      readonly dealt_seats?: ReadonlyArray<number>;
      /** Server-computed legal-action surface for the seat the hand
       *  opens on (`initial_to_act`) — the engine `legal_actions`
       *  authority mirrored on the wire. The LIVE-PATH delivery of the
       *  opener's exact action-bar bounds on the first turn of a hand
       *  (the reconnect-only `Snapshot.my_legal_actions` left this gap
       *  open). The bounds are PUBLIC table state, so they ride the
       *  shared broadcast frame; the reducer stores them ONLY when
       *  `initial_to_act === mySeat`. Optional so a pre-bump server is
       *  tolerated (the reducer then leaves `legal_actions` null and the
       *  bar fails closed until the next delivery). */
      readonly initial_legal?: LegalActionSetView | null;
      /** Wire minor 47 — the RECEIVING seat's own two hole cards.
       *
       *  Replaces `final_seed`, which this frame used to carry. The deck
       *  is a pure function of that seed, so every seated client could
       *  derive every opponent's pair and the whole unrevealed board; only
       *  a render gate kept them off screen. The server now sends each
       *  socket its own pair and nothing else, so the separation is in the
       *  bytes rather than in what the client agrees not to look at.
       *
       *  `null` / absent means "not yours to see": a spectator, an unseated
       *  subscriber, or a seat that was not dealt into this hand.
       */
      readonly my_cards?: readonly [CardView, CardView] | null;
    }
  | {
      readonly kind: "action_applied";
      readonly hand_seq: number;
      readonly action_seq: number;
      readonly seat: number;
      readonly action: ActionView;
      readonly pot: number;
      readonly round_bet: number;
      readonly next_to_act: number | null;
      /** Engine's current min-raise floor. Dynamic during a hand:
       *  starts at `bigBlind` pre-flop and grows by the most
       *  recent legal-raise delta after every accepted Bet / Raise
       *  / AllIn-that-counts-as-raise. Used by the FE's slider /
       *  preset / range-guard to surface the correct lower bound
       *  for the next bet/raise envelope. Optional on the TS side
       *  so a pre-bump server snapshot that omits the field still
       *  parses; the reducer falls back to `bigBlind` in that case. */
      readonly min_raise?: number;
      /** Server-computed legal-action surface for the seat the action
       *  handed the clock to (`next_to_act`) — the engine `legal_actions`
       *  authority mirrored on the wire. THIS is the live turn-handoff
       *  delivery: when an opponent acts and the clock passes to me, my
       *  exact server bounds arrive on the SAME frame. PUBLIC table
       *  state → rides the shared broadcast; the reducer stores it ONLY
       *  when `next_to_act === mySeat`. `null` when the action settled /
       *  pushed to the inter-hand pause. Optional so a pre-bump server
       *  is tolerated (bar fails closed until the next delivery). */
      readonly next_legal?: LegalActionSetView | null;
    }
  | {
      readonly kind: "stage_advanced";
      readonly hand_seq: number;
      readonly stage: StageView;
      readonly board: ReadonlyArray<CardView>;
      readonly pot: number;
      /** Wire minor 2 — post-stage-advance first-to-act seat.
       *  Optional so pre-bump frames (minor ≤ 1) still parse; the
       *  reducer preserves the prior `current_to_act` when this is
       *  `undefined`. */
      readonly next_to_act?: number | null;
      /** Server-computed legal-action surface for the new street's
       *  first actor (`next_to_act`) — the engine `legal_actions`
       *  authority mirrored on the wire. Same public-state / store-
       *  only-when-mine contract as `action_applied.next_legal`; the
       *  live delivery of the first-to-act bounds on a new street.
       *  `null` on a Showdown advance. Optional so a pre-bump server is
       *  tolerated. */
      readonly next_legal?: LegalActionSetView | null;
    }
  | {
      readonly kind: "round_closed";
      readonly hand_seq: number;
      readonly stage: StageView;
      readonly reason: RoundCloseReasonView;
      readonly pot: number;
      /** Wire minor 2 — authoritative next-actor seat. `null` for
       *  terminal closes (`all_folded` / `all_all_in`) where the
       *  hand is about to settle. Optional so pre-bump frames
       *  still parse. */
      readonly next_to_act?: number | null;
      /** Server-computed legal-action surface for the next actor after
       *  the round closed (`next_to_act`) — the engine `legal_actions`
       *  authority mirrored on the wire. Same public-state / store-
       *  only-when-mine contract as `action_applied.next_legal`. `null`
       *  on terminal closes. Optional so a pre-bump server is
       *  tolerated. */
      readonly next_legal?: LegalActionSetView | null;
    }
  | {
      readonly kind: "settled";
      readonly hand_seq: number;
      readonly awards: ReadonlyArray<AwardView>;
      readonly final_stacks: ReadonlyArray<SeatStackView>;
      /** Wire minor 7 (ADR-007 v1.0) — per-seat `committed_hand`
       *  snapshot at settle time. Drives `net = awarded - invested`
       *  on the SettledSummary banner. Optional so a pre-bump
       *  server (minor ≤ 6) still parses cleanly; the reducer
       *  falls back to the seat's existing `committed_hand` when
       *  the field is absent. */
      readonly invested?: ReadonlyArray<SettledInvestedView>;
      /** Side-pot tier breakdown (cash-game standard). Optional so
       *  a pre-bump server (no `pot_tiers`) still parses cleanly;
       *  the renderer falls back to the single-pot display. See
       *  `PotTier` doc for the per-tier shape. */
      readonly pot_tiers?: ReadonlyArray<PotTier>;
      /** Aggregate rake carved off the pot before distribution
       *  (sum across side-pot tiers; backend `RakeHook`). Zero in
       *  free-chip / `NoRakeStrategy` deploys. Optional so a pre-
       *  rake-hook server still parses cleanly; the caller's
       *  rakeback dashboard falls back to the local pot-based
       *  estimate when the field is absent. */
      readonly rake_amount?: number;
      /** Seats that reached the showdown without revealing
       *  their hole cards — the auto-muck losers under the
       *  PokerStars / GGPoker / partypoker / 888 default
       *  (every site auto-mucks losing hands unless the
       *  player explicitly opts to show via the
       *  `seat_revealed_hole` reveal path). Empty on the
       *  immediate-win `all_folded` settle (no showdown
       *  reached). Optional so pre-bump servers still
       *  parse — the reducer falls back to the empty set. */
      readonly mucked_seats?: ReadonlyArray<number>;
      /** Wire minor 50 — who took chips from a CONTESTED pot, as
       *  the same player hex `awards` uses.
       *
       *  Not derivable from `awards`, which is why it is here: an
       *  uncalled bet coming back is an award and is not a win, so
       *  reading a winner out of `awards` crowned the over-bettor who
       *  had just lost the pot. No arithmetic over `awards` and
       *  `invested` recovers the difference either — a seat can win a
       *  side pot for less than it put in.
       *
       *  `undefined` from a pre-bump server means "not told", not
       *  "nobody won": the caller shows no crown rather than guessing
       *  from the money again. */
      readonly winners?: ReadonlyArray<string>;
    }
  // player presence (wire minor 4) — broadcast on `/tables/:id/join`
  // success so the seated roster is observable mid-lobby, not just
  // mid-hand. Idempotent re-join is silent on the wire; the variant
  // surfaces only for the first attach of a `(seat, player_id)`
  // pair. Username comes from the account row when resolved, `null`
  // otherwise (renderer falls back to the short ed25519 hex slice).
  | {
      readonly kind: "player_joined";
      readonly seat: number;
      readonly player_id: PlayerIdHex;
      readonly username: string | null;
      /** Wire minor 39 — the seat stack the join committed (the
       *  route's `buy_in`, which IS the seat stack). Carried so the
       *  reducer can write a real SEAT row, not just a roster row: a
       *  player who sits down mid-hand was otherwise drawn as an OPEN
       *  SEAT on every other client until the next `HandStarted`.
       *  `undefined` from a pre-bump server — the reducer keeps the
       *  roster-only behavior in that case. */
      readonly stack?: number;
      /** Wire minor 39 — whether the seat landed OUTSIDE the live
       *  hand. A mid-hand join is seated `SittingOut` and dealt in at
       *  the next boundary; an off-hand join is seated `Active`. The
       *  server's own `round_in_progress` verdict, not a client
       *  re-derivation from the local phase. `undefined` pre-bump. */
      readonly sitting_out?: boolean;
    }
  // Symmetric counterpart to `player_joined` — broadcast on a
  // successful `POST /tables/:id/leave` so other clients drop the
  // departing seat's roster row (avatar / username / chip stack)
  // without waiting for the next HandStarted. `username` mirrors
  // the resolved display name at leave time; the renderer falls
  // back to the short ed25519 hex slice when omitted.
  //
  // Wire minor 24 — `forfeit_reason` field. Present when the
  // leave happened mid-hand AND the actor synthesised a fold +
  // committed-chip forfeit on the way out (BE-LEAVE-UNIFY
  // `MidHandFold` arm). Absent / `null` for inter-hand leaves
  // (no chip-forfeit cue needed). Today only `"voluntary"` is
  // emitted; the union widens with explicit i18n strings as
  // operator-driven leave causes land (kick / disconnect-timeout
  // / regulatory shutdown) without renaming the existing arm.
  // `reason` (BE `PlayerLeftReason`) says WHY the seat went vacant.
  // The leave cue itself is still driven by `forfeit_reason`; the
  // client reads `reason` for the one case where the seat vanishing
  // silently would be wrong — `"busted"`, the server reclaiming a
  // chair from a player who is sitting right there watching it
  // happen. Every other arm is declared so the union is the wire's
  // and not a subset, and unknown arms stay tolerated.
  | {
      readonly kind: "player_left";
      readonly seat: number;
      readonly player_id: PlayerIdHex;
      readonly username: string | null;
      readonly forfeit_reason?: "voluntary" | null;
      readonly reason?: PlayerLeftReason | null;
    }
  // inter-hand transition (wire minor 5) — broadcast on
  // `/tables/:id/next-hand` success so non-host peers drop their
  // settled phase the moment the host arms the next hand, not when
  // the eventual HandStarted lands after the seed round. The
  // `next_hand_seq` matches the upcoming hand's `HandStarted.hand_seq`
  // (server invariant — bump happens inside
  // `finalize_seed_and_start_hand`, the broadcast value is
  // `actor.hand_seq + 1`). `button_seat` is the post-rotation button
  // index; the eventual HandStarted overwrites it idempotently.
  | {
      readonly kind: "next_hand_started";
      readonly next_hand_seq: number;
      readonly button_seat: number;
    }
  // host ownership transfer (wire minor 9) — broadcast when the
  // server-side host state changes (initial assignment, AFK
  // timeout, busted seat). The reducer mirrors the new
  // `new_host_seat` into the store's `host_seat` field +
  // surfaces a transient banner to every observer. `transfer_seq`
  // is the per-table monotonic dedup key; `old_host_seat = null`
  // marks the initial assignment (no prior host). `server_unix_ms`
  // anchors the AFK countdown the new host sees.
  | {
      readonly kind: "host_ownership_transferred";
      readonly transfer_seq: number;
      readonly old_host_seat: number | null;
      readonly new_host_seat: number;
      readonly reason: "afk_timeout" | "busted";
      readonly server_unix_ms: number;
    }
  // action timer (wire minor 8) — broadcast every time the server's
  // ActionTimer driver puts a seat on the clock (HandStarted /
  // StageAdvanced / post-ActionApplied re-arm). `deadline_unix_ms`
  // is the Unix-ms boundary the server-side timer will fire on;
  // `server_unix_ms` is the server's wall clock at the moment of
  // broadcast so the client can compute a skew anchor and tick
  // smoothly even when its local clock drifts.
  | {
      readonly kind: "timer_armed";
      readonly seat: number;
      readonly deadline_unix_ms: number;
      readonly server_unix_ms: number;
      /** Base-window deadline (`armed_at + 25s`) the action ring
       *  drains over, distinct from `deadline_unix_ms` which stays
       *  the 85s server-side enforcement boundary. Optional + serde-
       *  defaulted to `0` on the BE: a pre-cutover server omits it
       *  (or emits `0`), in which case the reducer's sanity guard
       *  leaves the field undefined and the ring falls back to the
       *  85s `deadline_unix_ms` window. */
      readonly action_deadline_unix_ms?: number;
      /** Wall-clock millis at which this window was ARMED. The ring's
       *  denominator: `action_deadline − armed_at` is the window's
       *  LENGTH, and unlike `action_deadline − server_unix_ms` it does
       *  not depend on when this particular frame was sent. Without it
       *  a re-broadcast reads as a shorter window (the ring refills)
       *  and a peer that connected mid-turn reads a full ring on a
       *  window that is half gone. `undefined` on a pre-bump server —
       *  the projection then falls back to the frame-derived length. */
      readonly armed_at_unix_ms?: number;
    }
  // action timer expiry (wire minor 8) — broadcast when the server-
  // side timer fires before the seat acts. `action` is the auto-
  // chosen response (check-when-free OR fold-otherwise per the
  // backend's auto-action policy); the receiving reducer clears
  // `active_timer` + surfaces a transient "시간 초과" cue so other
  // players see the auto-action without waiting for the eventual
  // `ActionApplied` frame.
  | {
      readonly kind: "timer_expired";
      readonly seat: number;
      readonly action: ActionView;
    }
  // showdown reveal (wire minor 6) — broadcast on `table_action`
  // post-Settled when the hand reached `Stage::Showdown` (natural
  // River close OR all-in run-out). One frame per settled-at-
  // showdown hand; the `AllFolded` immediate-win settle path
  // emits 0 frames (no showdown reached, no reveal owed). Both WS
  // lanes carry this variant identically (`to_spectator_view`
  // identity pass-through) — the global poker standard reveals
  // post-decision hole cards equally to seated players and read-
  // only spectators. Idempotent key = `hand_seq` (state-replace
  // on the receiving reducer).
  | {
      readonly kind: "hand_showdown";
      readonly hand_seq: number;
      readonly seats: ReadonlyArray<HandShowdownSeatView>;
      readonly community: ReadonlyArray<CardView>;
    }
  // Wire minor 41 — hands tabled because no further betting is
  // possible (the `AllAllIn` round close). Every seat still in the hand
  // is all-in or has called one, so the outcome is decided and the
  // board is only waiting to be dealt; every room turns the cards face
  // up here and runs the board out under them.
  //
  // Distinct from `hand_showdown`, which lands at SETTLE and applies
  // the auto-muck policy (winner shows, losers conceal). Nobody has won
  // yet at this point, so every seat still in the hand is tabled, and
  // `hand_rank` is empty — there is no final rank until the board is
  // complete.
  | {
      readonly kind: "all_in_tabled";
      readonly hand_seq: number;
      readonly seats: ReadonlyArray<HandShowdownSeatView>;
    }
  // a seat topped up (wire minor 46). The credit is durable the instant
  // the `/rebuy` route returns, and it used to tell nobody: the caller's
  // own felt kept the old stack until some later frame happened to
  // carry a new one — the next deal at best, and never at a table too
  // short of chips to deal, which is the table a top-up is for.
  // Carries every fact the credit changed so no surface has to infer
  // one.
  | {
      readonly kind: "seat_rebought";
      readonly seat_idx: number;
      readonly player_id: PlayerIdHex;
      readonly new_stack: number;
      /** The seat's state AFTER the credit — a busted seat recovers to
       *  active between rounds. */
      readonly sitting_out: boolean;
      /** The parked flip after the credit: `false` is a recovery queued
       *  to the next hand boundary, `null` nothing queued. */
      readonly pending_sit_out?: boolean | null;
    }
  // Minor 51 adds `wallet_changed`. PRIVATE — the server addresses it
  // to one socket, so a balance never reaches the rest of the table.
  //
  // `seat_rebought` above fixed this defect on the other side of the
  // same route: the chips a top-up moves come OUT of the wallet, and
  // the wallet was told nothing. The title-bar badge kept the figure
  // it read in the lobby for the whole sitting, and the auto-top-up
  // sized its next request from that same stale number.
  // Wire minor 52 — the part of an all-in nobody could call, back on
  // the bettor's stack as the action closes into the run-out. `pot` is
  // the pot after the return.
  | {
      readonly kind: "uncalled_returned";
      readonly hand_seq: number;
      readonly seat: number;
      readonly amount: number;
      readonly pot: number;
    }
  | {
      readonly kind: "wallet_changed";
      /** Spendable, off the table. */
      readonly free: number;
      /** Chips at tables right now — the sum of live seat stacks.
       *  Moves with every pot, so `free + in_play` is always what
       *  the player is worth. */
      readonly in_play: number;
      /** Locked behind seats, every table included. */
      readonly held: number;
    }
  // spectator membership (wire minor 3) — ride the same WS lane as
  // every other game frame for both player and spectator subscribers.
  | {
      readonly kind: "spectator_joined";
      readonly player_id: PlayerIdHex;
      readonly username: string | null;
    }
  | { readonly kind: "spectator_left"; readonly player_id: PlayerIdHex }
  // spectator-lane rewrites (wire minor 3) — surface ONLY on
  // spectator-mode WS connections, never on the player lane. Carry
  // every field of their player-lane analogues except `final_seed`.
  | {
      readonly kind: "spectator_hand_started";
      readonly hand_id: number;
      readonly hand_seq: number;
      readonly button: number;
      readonly blinds: BlindsView;
      readonly seats: ReadonlyArray<SeatStartedView>;
      readonly initial_to_act: number;
      readonly round_bet: number;
      readonly actions_taken: number;
      /** Wire minor 34 — mirror of `HandStarted.dealt_seats` for
       *  structural parity. Public table state, so it rides the
       *  spectator lane identically, but INERT here: hole-card
       *  re-derivation needs `final_seed`, which this variant
       *  structurally omits, so the roster can never become hole cards
       *  on the spectator path. Optional for forward-compat. */
      readonly dealt_seats?: ReadonlyArray<number>;
      // NO `final_seed` — spectator-lane forbidden boundary.
    }
  | {
      readonly kind: "spectator_snapshot";
      readonly hand: SpectatorHandSnapshot | null;
      /** Spectator-lane mirror of `Snapshot.seed_in_flight`. Same
       *  payload on both lanes — seed-round liveness is public table
       *  state. Optional for forward-compat with a pre-minor-18
       *  server. */
      readonly seed_in_flight?: SeedRoundView | null;
      /** Spectator-lane mirror of `Snapshot.occupied_seats` (wire
       *  minor 31). Seat occupancy is public table state, so the
       *  full live roster rides the spectator lane too — letting a
       *  spectator who reconnects into the inter-hand pause rebuild
       *  the same membership map a seated player gets. Optional for
       *  forward-compat. */
      readonly occupied_seats?: ReadonlyArray<SeatOwnerSlot>;
      /** Wire minor 38 — spectator-lane mirror of `Snapshot.hand_seq`.
       *  Public table state, so the current hand number rides the
       *  spectator lane identically — a spectator reconnecting into
       *  the inter-hand pause renders the correct "핸드 #N" instead of
       *  #0. Optional for forward-compat. */
      readonly hand_seq?: number;
      /** Spectator-lane mirror of `Snapshot.max_seats` (wire minor
       *  39). The spectator camera draws the same fixed chair ring, so
       *  it needs the same geometry input on the first frame. */
      readonly max_seats?: number;
    }
  // WS-lane authentication (wire minor 12 / ADR-007 v1.6).
  // The server emits `AuthChallenge` as its first post-`ProtocolVersion`
  // frame; the client signs `BLAKE3(DOMAIN_WS_AUTH || table_id ||
  // nonce || server_unix_ms_le)` and replies with an `AuthResponse`
  // ClientCommand. The server then emits `AuthOk` (followed by the
  // standard `Snapshot`) or `AuthFail` + closes the socket. Nonces
  // are hex-encoded 32-byte challenges with a 30-second TTL +
  // single-use consume on the server side.
  | {
      readonly kind: "auth_challenge";
      /** 32-byte nonce, lowercase hex. Re-issued on every fresh
       *  WS connection (and on every reconnect — the nonce store
       *  drops single-use entries past the TTL). */
      readonly nonce: string;
      /** Server wall-clock at challenge issue (ms since epoch).
       *  Binds the sig payload so a captured AuthResponse can't
       *  replay against a different challenge window. */
      readonly server_unix_ms: number;
    }
  | {
      readonly kind: "auth_ok";
      /** The lane the server granted, resolved AUTHORITATIVELY from the
       *  live seated roster (seated → player, registered spectator →
       *  spectator) — NOT from any client-supplied URL hint. Carried on
       *  the wire so the client can assert the granted lane. */
      readonly lane: WsLane;
      /** Opaque single-active-surface token (policy 2). 32 lowercase-
       *  hex chars identifying the WS slot this handshake just claimed
       *  (mirrors BE `AuthOk.surface`). The client stamps it on every
       *  mutating table-scoped HTTP request via `x-surface-id` so the
       *  server can reject a mutation from a displaced/older surface
       *  (`403 session.superseded`). Optional for back-compat: a
       *  pre-policy-2 server omits it, and an absent token means the
       *  HTTP gate stays in its allow-by-default mode. */
      readonly surface?: string;
    }
  | {
      readonly kind: "auth_fail";
      /** Backend-supplied Korean failure reason (the client
       *  surfaces it verbatim in the AuthFail toast). The
       *  WIRE_FORMAT_MINOR bump signals the reload prompt is
       *  in scope. */
      readonly reason: string;
    }
  // seed-protocol observability quartet (wire minor 18). Replaces
  // the legacy FE polling loops: SeedRoundOpened cues the joiner
  // to send its single contribute POST; SeedContribLanded is a
  // progress tick for the "n / m contributions received" overlay
  // (the contribution bytes themselves never ride the wire);
  // SeedRevealReady marks the auto-chain's reveal step (no FE
  // action needed); SeedFinalized is the FE overlay's primary
  // dismiss trigger (HandStarted serves as the dual-trigger
  // fallback when the matching SeedFinalized frame was lost in
  // transit).
  | {
      readonly kind: "seed_round_opened";
      /** Upcoming hand's seq (= BE actor `hand_seq + 1` at commit
       *  time). Contributors bind this into their signed
       *  `SeedContribEnvelope`. */
      readonly hand_seq: number;
      /** 32-byte BLAKE3 commit sealed by `server_commit`. Rides as
       *  the raw byte array (same JSON-array-of-ints encoding as
       *  `HandStarted.final_seed`). */
      readonly commit: ReadonlyArray<number>;
      /** Number of contributions the round will accept before the
       *  auto-chain fires. The progress overlay renders
       *  `count_so_far / expected_player_count` against this. */
      readonly expected_player_count: number;
      /** The roster that count is the size of (wire minor 49) — the
       *  players this round's commit attests, and therefore the only
       *  answer to "was I asked". Absent from a pre-bump server;
       *  `seedRole` owns that reading. */
      readonly expected_player_ids?: ReadonlyArray<string>;
    }
  | {
      readonly kind: "seed_contrib_landed";
      readonly hand_seq: number;
      /** 32-byte ed25519 verifying key (lowercase hex) — the
       *  contributor whose envelope the BE just accepted. */
      readonly player_id: PlayerIdHex;
      /** Running progress (inclusive of this contribution). */
      readonly count_so_far: number;
      /** Stable expected-set size sealed at `server_commit` time. */
      readonly count_total: number;
    }
  | { readonly kind: "seed_reveal_ready"; readonly hand_seq: number }
  | { readonly kind: "seed_finalized"; readonly hand_seq: number }
  // stale-seed sweep abort (wire minor 19). BE's sweep job fires
  // this when expected contributors miss the threshold window; the
  // missing player_ids list identifies the seats the BE flipped
  // to sit-out. Idempotent key = hand_seq (one abort per opened
  // round at most). The FE reducer clears the seed_in_flight
  // projection + stamps the aborted seq for the orchestrator's
  // transition guard.
  | {
      readonly kind: "seed_round_aborted";
      readonly hand_seq: number;
      readonly missing_player_ids: ReadonlyArray<PlayerIdHex>;
      readonly reason: SeedAbortReason;
    }
  // reconnect (player lane). `seed_in_flight` is `Some` iff the actor
  // has an open seed round at subscribe time (between the
  // `seed_round_opened` broadcast and the auto-chain's
  // `seed_finalized`). Optional on the TS side so a pre-minor-18
  // server omits the field cleanly via structural typing — the
  // reducer treats the absent field as "no round in flight".
  | {
      readonly kind: "snapshot";
      readonly hand: HandSnapshot | null;
      readonly seed_in_flight?: SeedRoundView | null;
      /** Wire minor 31 — LIVE seat→player occupancy at subscribe
       *  time (the FULL roster). Load-bearing for a reconnect that
       *  lands in the inter-hand pause (`hand: null`): the per-seat
       *  `owner` fields ride only inside a live `HandSnapshot`, so
       *  this is the only membership source on the `hand: null`
       *  branch. Optional for forward-compat — a pre-bump server
       *  omits it and the reducer treats it as an empty roster (those
       *  seats render via the short-hex fallback). */
      readonly occupied_seats?: ReadonlyArray<SeatOwnerSlot>;
      /** Wire minor 38 — the table's CURRENT `hand_seq` (= actor
       *  `hand_seq`, the last-started hand number; `0` on a fresh
       *  table). Carried at the top level so a client landing in the
       *  inter-hand pause (`hand: null`) hydrates the real hand number
       *  instead of resetting to 0 — without it a late joiner /
       *  reconnect targets the wrong seed round (`captured + 1`) and
       *  never contributes, stranding the round until the stale-seed
       *  sweep sits them out. Optional for forward-compat: a pre-bump
       *  server omits it and the reducer falls back to deriving it
       *  from the in-flight seed round (`seed_in_flight.hand_seq - 1`)
       *  or `0`. */
      readonly hand_seq?: number;
      /** Wire minor 39 — the table's seat CAPACITY (how many chairs
       *  it has, BE-validated 2..=9), not how many are warm.
       *
       *  The felt draws a fixed chair ring, so the capacity is an
       *  INPUT to seat geometry. It previously reached the client only
       *  through the one-shot `GET /tables/:id/state` config read,
       *  which can resolve AFTER this frame — leaving a window where
       *  the seat map was populated and the chair count was still `0`,
       *  and re-rotating the whole ring the moment the read landed.
       *  Carried here, geometry is computable from the first frame.
       *  Optional for forward-compat: a pre-bump server omits it and
       *  the store keeps whatever the HTTP read supplies. */
      readonly max_seats?: number;
    }
  // table-lifecycle dismiss frame (wire minor 21). Emitted at most
  // once per per-table broadcast channel — the BE publishes it
  // immediately before dropping the per-table sender, so dedup is
  // wire-level. The `cause` carries the lifecycle reason; the
  // `dissolved_at_ms` BE wall-clock anchors the dismiss modal's
  // "X분 X초 전 해체" label across socket-buffer delays. No
  // further frame can arrive on the same lane after this one.
  | {
      readonly kind: "table_dissolved";
      readonly cause: TableDissolvedCause;
      readonly dissolved_at_ms: number;
    }
  // operator-issued kick (wire minor 22). Force-removes a single
  // seat without tearing the whole table down. The kicked user's
  // FE flips `phase` to `"kicked"` + surfaces the dismiss modal;
  // every other connected client renders an action-log line and
  // continues. Mid-hand kicks funnel through a BE-side synthetic
  // Fold so the round-close cascade stays consistent before the
  // seat vacates. `admin_account_id` is the operator's BE-side
  // account row; today the FE just carries it through to the
  // action-log line so a future admin-roster lookup can resolve
  // it to a username. `(seat_idx, kicked_at_ms)` is the idempotent
  // key — a duplicate frame with the same pair is a no-op on the
  // receiving reducer.
  | {
      readonly kind: "player_kicked";
      readonly seat_idx: number;
      readonly player_id: PlayerIdHex;
      readonly admin_account_id: number;
      readonly reason: string;
      readonly kicked_at_ms: number;
    }
  // user-driven sit-out flip (wire minor 23). The seated player
  // flipped their own SittingOut state via `POST
  // /tables/:id/seat/sit-out` or `/sit-back`. The `queued`
  // discriminator routes the HUD badge: `false` = the flip
  // applied to seat state immediately (off-hand flip), `true` =
  // the flip is queued on `pending_sit_out` for the next
  // start-next-hand boundary to drain (mid-hand flip). Idempotent
  // wire-level by `(seat_idx, flipped_at_ms)` — a snapshot-then-
  // tail replay collapses the duplicate.
  | {
      readonly kind: "seat_sit_out_flipped";
      readonly seat_idx: number;
      readonly player_id: PlayerIdHex;
      readonly sit_out: boolean;
      readonly queued: boolean;
      readonly flipped_at_ms: number;
    }
  // Wire minor 26 — table chat broadcast. Published by the BE
  // `POST /tables/:id/chat` route after the per-sender 2 s
  // throttle + sanitiser gates pass. Server-stamped
  // `sent_at_ms` (Unix ms) anchors the idempotent key with
  // `seat_idx` so a snapshot-then-tail replay collapses
  // duplicates. `username` carries the cached display name
  // looked up server-side at publish time; falls back to a
  // seat label on the renderer side when null. Pre-bump
  // clients ignore via the unknown-kind graceful skip.
  | {
      readonly kind: "chat_broadcast";
      readonly seat_idx: number;
      readonly player_id: PlayerIdHex;
      readonly username: string | null;
      readonly message: string;
      readonly sent_at_ms: number;
    }
  // Wire minor 28 — engine-side post-resolve reject. The BE
  // emits this when an `ActionApplied` would breach a
  // settle-invariant (chip-conservation, all-in math, stack
  // underflow, etc.). The `reason` field carries the
  // invariant class as a stable wire token (e.g.
  // `"settle_invariant"`) so the FE renders a targeted
  // Korean toast via `lib/server_error.ts`
  // `mapServerErrorToToast` instead of a generic banner. The
  // reducer stamps `last_hand_error = { hand_seq, reason }`
  // and otherwise leaves state alone — the BE recovers via
  // the next `NextHandStarted` cue. Mirrors BE
  // `hub/events.rs:322 HandError { hand_seq: u64, reason: String }`.
  | {
      readonly kind: "hand_error";
      readonly hand_seq: number;
      readonly reason: string;
    }
  // Wire minor 28 — server-initiated singleton-slot teardown.
  // Emitted immediately before the BE closes a connection
  // whose `(table_id, player_id)` slot was claimed by a
  // newer authed socket. The reducer records the SURFACE
  // signal `connection.disconnect = { kind: "surface_displaced",
  // reason }` so the WS client's on-close handler stops the
  // reconnect loop + the Game-level router halts this surface
  // (take-over notice) WITHOUT a logout. `markReconnected`
  // restores it to `live`. Mirrors BE
  // `hub/events.rs:333 Displaced { reason: String }`.
  | { readonly kind: "displaced"; readonly reason: string }
  // Account-wide session revoke. Fanned to every WS the
  // account had open when a new login (latest-wins) or an
  // explicit logout revokes the prior session-cookie. The
  // FE reducer records the DISTINCT signal `connection.disconnect
  // = { kind: "session_revoked", reason, revoked_at_ms }` — the
  // only signal the Game-level router routes to the logout
  // cascade (`markSessionRevoked`). Separate union slot from
  // `displaced` so the two facts can never share a handler.
  // Mirrors BE `hub/events.rs:852 SessionRevoked { reason:
  // String, revoked_at_ms: u64 }`.
  | {
      readonly kind: "session_revoked";
      readonly reason: string;
      readonly revoked_at_ms: number;
    }
  // Voluntary post-settle hole reveal. Published after the BE
  // accepts a `POST /tables/:id/seat/reveal-hole` call so every
  // observer at the table sees the cards the caller opted to
  // expose. Mirrors the PokerStars / GGPoker / partypoker /
  // 888 "Show cards" convention — uncontested winners can
  // prove a bluff, showdown losers can prove a missed
  // bluff-catch, and either feeds the table-talk dynamic.
  // Single-shot per `(hand_seq, seat_idx)` (route-layer dedup);
  // pre-bump clients drop the variant via the unknown-kind
  // graceful-skip rule. Mirrors BE
  // `hub/events.rs:886 SeatRevealedHole { hand_seq, seat_idx,
  // hole_cards }`.
  | {
      readonly kind: "seat_revealed_hole";
      readonly hand_seq: number;
      readonly seat_idx: number;
      readonly hole_cards: readonly [CardView, CardView];
    };

/**
 * Reason the BE side tore the table down (wire minor 21). Tagged
 * enum with `snake_case` `kind` discriminator — same shape as
 * `SeedAbortReason`. Future variants (host-driven kill, regulatory
 * shutdown, scheduled maintenance) extend the tail without
 * renaming existing arms.
 *
 *   - `lived_then_vacated` — table played at least one hand
 *     (`hand_seq > 0`) and drained to zero seats; both the
 *     route-layer last-player-leave path and the timer-driven
 *     sweep on a post-game vacated table funnel here.
 *   - `creator_abandon` — table created via `POST /tables` but
 *     never had a hand land (`hand_seq == 0`); aged out past the
 *     stale-empty grace window or its sole joiner left before
 *     play started.
 *   - `admin_force_dissolve` — operator force-killed via the
 *     admin API. The `reason` string carries the operator's
 *     free-form note for the dismiss modal to surface verbatim.
 */
export type TableDissolvedCause =
  | { readonly kind: "lived_then_vacated" }
  | { readonly kind: "creator_abandon" }
  | { readonly kind: "admin_force_dissolve"; readonly reason: string };

/**
 * Discriminator literal-union extracted from the `WsEvent` tagged
 * union. Re-exported so consumers (parse / dispatcher / dedup
 * helpers) can key against the canonical kind set without
 * re-declaring the literal list — single source of truth lives at
 * the union definition above.
 */
export type WsEventKind = WsEvent["kind"];

/**
 * Compile-time exhaustive tuple of every `WsEventKind`. Two
 * type-system gates flag a future drift:
 *
 *   1. **Forward gate** (`satisfies readonly WsEventKind[]`) — a
 *      typo or stale literal that doesn't appear on the union
 *      lands as a TS error on the offending array entry.
 *   2. **Reverse gate** (the `_ALL_WS_EVENT_KINDS_EXHAUSTIVE`
 *      assertion below) — a new `WsEvent` variant whose
 *      discriminator is missing from this tuple collapses the
 *      `Exclude<...>` residue from `never` to the missing
 *      literal(s); the `true` initializer can no longer satisfy
 *      the resulting tuple-shape error, so the omission lands
 *      as a TS compile error pinned to the missing variant
 *      name(s).
 *
 * Together these replace the prior hand-maintained Set of strings
 * the parser used to gate unknown-kind graceful-skip — the wire
 * union is now the single source of truth, and the dedup Set
 * below is derived from this tuple at module load.
 */
export const ALL_WS_EVENT_KINDS = [
  "protocol_version",
  "heartbeat",
  "lagged",
  "shutdown",
  "hand_started",
  "action_applied",
  "stage_advanced",
  "round_closed",
  "settled",
  "spectator_joined",
  "spectator_left",
  "spectator_hand_started",
  "spectator_snapshot",
  "player_joined",
  "player_left",
  "next_hand_started",
  "hand_showdown",
  "timer_armed",
  "timer_expired",
  "host_ownership_transferred",
  "auth_challenge",
  "auth_ok",
  "auth_fail",
  "seed_round_opened",
  "seed_contrib_landed",
  "seed_reveal_ready",
  "seed_finalized",
  "seed_round_aborted",
  "snapshot",
  "table_dissolved",
  "player_kicked",
  "seat_sit_out_flipped",
  "chat_broadcast",
  "hand_error",
  "displaced",
  "session_revoked",
  "seat_revealed_hole",
  "all_in_tabled",
  "seat_rebought",
  "wallet_changed",
  "uncalled_returned",
] as const satisfies readonly WsEventKind[];

// Reverse-direction exhaustiveness check. When every `WsEventKind`
// appears in `ALL_WS_EVENT_KINDS`, the `Exclude` residue collapses
// to `never` and the `_Exhaustive` type resolves to `true`,
// satisfying the `true` initializer. If a future variant is
// added to the union but forgotten here, `Exclude` resolves to
// the missing literal(s) and `_Exhaustive` becomes the tuple-
// shape error type — `true` no longer assigns, and TS pins the
// failure on the missing variant name in the error message.
type _MissingWsEventKinds = Exclude<
  WsEventKind,
  (typeof ALL_WS_EVENT_KINDS)[number]
>;
type _AllWsEventKindsExhaustive = [_MissingWsEventKinds] extends [never]
  ? true
  : ["_missing_ws_event_kinds:", _MissingWsEventKinds];
const _ALL_WS_EVENT_KINDS_EXHAUSTIVE: _AllWsEventKindsExhaustive = true;
// Reference the const so the type-only check doesn't trip
// unused-binding lints under a future eslint config.
void _ALL_WS_EVENT_KINDS_EXHAUSTIVE;

/**
 * Variants we already know how to apply. Used to gate unknown-
 * kind graceful skip (forward-compat minor bumps). Derived from
 * `ALL_WS_EVENT_KINDS` so a new variant lands in the set
 * automatically once the tuple above is updated.
 *
 * The element type stays `string` (not `WsEventKind`) because the
 * parser's `.has(...)` call receives the raw `kind` field off the
 * decoded JSON, which TypeScript types as `string`. Widening here
 * keeps the consumer-side cast surface inside this module —
 * exhaustiveness already lives on the tuple above.
 */
export const KNOWN_WS_EVENT_KINDS: ReadonlySet<string> = new Set(
  ALL_WS_EVENT_KINDS,
);
